/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Provider, NgZone, Injector } from '@angular/core';
import { PluginsNotificationService } from 'plugins-notification/lib/plugins-notification.service';
import { VegaHelperService } from 'utils/lib/platform/vega/vega-helper.service';
import { AndroidHelperService } from '../android-helper/android-helper.service';
import { LoggingService } from '../logging/logging.service';
import { PlatformDetectorService } from '../platform-detector/platform-detector.service';
import { AndroidPluginManager } from './android-plugin/android-plugin-manager.service';
import { AndroidService } from './android.service';
import { BridgeMockService } from './bridge.mocks.service';
import { BridgeService } from './bridge.service';
import { BrowserPluginManager } from './browser-plugin/browser-plugin-manager.service';
import { BrowserService } from './browser.service';
import { CefService } from './cef.service';
import { YggdrasilService } from './yggdrasil.service';
import { VegaPluginManager } from './vega-plugin/vega-plugin-manager.service';
import { VegaPluginService } from './vega-plugin.service';

export function createBridgeService(
    loggingService: LoggingService,
    ngZone: NgZone,
    bridgeMockService: BridgeMockService,
    injector: Injector,
    pluginsNotificationService: PluginsNotificationService
) {
    if (PlatformDetectorService.isCefPlatform()) {
        return new CefService(loggingService, pluginsNotificationService, ngZone, bridgeMockService);
    } else if (PlatformDetectorService.isAndroidPlatform()) {
        const browserPluginManager: BrowserPluginManager = injector.get(BrowserPluginManager);
        const androidPluginManager: AndroidPluginManager = injector.get(AndroidPluginManager);
        const androidHelperService: AndroidHelperService = injector.get(AndroidHelperService);
        return new AndroidService(
            loggingService,
            androidPluginManager,
            browserPluginManager,
            androidHelperService,
            bridgeMockService
        );
    } else if (VegaHelperService.isVegaWebViewAvailable()) {
        const browserPluginManager: BrowserPluginManager = injector.get(BrowserPluginManager);
        const vegaPluginManager: VegaPluginManager = injector.get(VegaPluginManager);
        const vegaHelperService: VegaHelperService = injector.get(VegaHelperService);
        return new VegaPluginService(
            loggingService,
            vegaPluginManager,
            browserPluginManager,
            vegaHelperService,
            bridgeMockService
        );
    } else if (PlatformDetectorService.isBrowserPlatform()) {
        const browserPluginManager: BrowserPluginManager = injector.get(BrowserPluginManager);
        return new BrowserService(loggingService, browserPluginManager, bridgeMockService);
    }
    return new YggdrasilService(loggingService, ngZone, bridgeMockService);
}

export const BridgeServiceProvider: Provider = {
    provide: BridgeService,
    useFactory: createBridgeService,
    deps: [LoggingService, NgZone, BridgeMockService, Injector, PluginsNotificationService]
};
